import { useCallback, useEffect, useState } from 'react';
import apiService from '@/services/apiService';
import type { QuoteItem } from '@/types/api';
import { SupportedProductType } from '@/types/api';
import type { OccupationalHealthServiceQuote } from '@/types/app';

export interface AddOnQuoteLists {
	occupationalHealthServiceQuotes: OccupationalHealthServiceQuote[];
	wgaExtendedQuotes: QuoteItem[];
	wgaQuotes: QuoteItem[];
	wiaExcessQuotes: QuoteItem[];
}

export interface UseAddOnQuotesResult extends AddOnQuoteLists {
	isLoading: boolean;
	/** Raw fetch error, or null. Callers pick their own user-facing message. */
	error: unknown;
	refetch: () => void;
}

/**
 * GETs the four add-on product quote lists (occupational health, WGA-gap,
 * WGA-gap-extended, WIA-excess) for a form, skipping any product type the form
 * doesn't include. Read-only: it never PATCH-triggers a new quote run.
 *
 * Shared by the customer's package overview (`Overview/Summary`) and the
 * read-only request overview (`useRequestQuotes`) so the endpoint set and the
 * result unwrapping can't drift between them. The raw error is surfaced as-is;
 * each caller maps it to its own message (the customer flow shows a generic
 * error, the advisor overview never exposes raw fetch messages).
 */
const useAddOnQuotes = (
	id: string | undefined,
	productTypes: SupportedProductType[]
): UseAddOnQuotesResult => {
	const [occupationalHealthServiceQuotes, setOccupationalHealthServiceQuotes] = useState<
		OccupationalHealthServiceQuote[]
	>([]);
	const [wgaExtendedQuotes, setWgaExtendedQuotes] = useState<QuoteItem[]>([]);
	const [wgaQuotes, setWgaQuotes] = useState<QuoteItem[]>([]);
	const [wiaExcessQuotes, setWiaExcessQuotes] = useState<QuoteItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<unknown>(null);

	const fetchQuotes = useCallback(async () => {
		if (!id) return;
		setIsLoading(true);
		setError(null);

		try {
			const [occupationalHealthServiceResult, wgaExtendedResult, wgaResult, wiaExcessResult] =
				await Promise.all([
					productTypes.includes(SupportedProductType.OCCUPATIONAL_HEALTH_SERVICE)
						? apiService.quote.occupational_health_service.fetch(id)
						: Promise.resolve([]),
					productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED)
						? apiService.quote.byProductType.fetch(
								id,
								SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED
							)
						: Promise.resolve(null),
					productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP)
						? apiService.quote.byProductType.fetch(
								id,
								SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP
							)
						: Promise.resolve(null),
					productTypes.includes(SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS)
						? apiService.quote.byProductType.fetch(
								id,
								SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS
							)
						: Promise.resolve(null)
				]);

			setOccupationalHealthServiceQuotes(occupationalHealthServiceResult);
			setWgaExtendedQuotes(
				wgaExtendedResult?.quotes[SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP_EXTENDED] ?? []
			);
			setWgaQuotes(wgaResult?.quotes[SupportedProductType.SICK_LEAVE_LONG_TERM_WGA_GAP] ?? []);
			setWiaExcessQuotes(
				wiaExcessResult?.quotes[SupportedProductType.SICK_LEAVE_LONG_TERM_WIA_EXCESS] ?? []
			);
		} catch (fetchError) {
			setError(fetchError);
		} finally {
			setIsLoading(false);
		}
	}, [id, productTypes]);

	useEffect(() => {
		fetchQuotes();
	}, [fetchQuotes]);

	return {
		occupationalHealthServiceQuotes,
		wgaExtendedQuotes,
		wgaQuotes,
		wiaExcessQuotes,
		isLoading,
		error,
		refetch: fetchQuotes
	};
};

export default useAddOnQuotes;
